import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { Subject, takeUntil } from "rxjs";

import { environment } from "../../../../../environments/environment";

import {
  SupportService,
  SupportTicket,
  SupportMessage,
} from "../../../../shared/services/support.service";

type SupportCategory =
  | "appointments"
  | "payments"
  | "account"
  | "technical"
  | "salon"
  | "billing"
  | "other";

interface SupportForm {
  name: string;
  email: string;
  category: SupportCategory | "";
  subject: string;
  description: string;
}

interface SupportCategoryOption {
  value: SupportCategory;
  label: string;
}

interface CurrentUser {
  full_name?: string;
  email?: string;
  role_name?: string;
}

@Component({
  selector: "app-support",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: "./support.component.html",
  styleUrl: "./support.component.scss",
})
export class SupportComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly apiUrl = environment.apiUrl;

  // =========================================================
  // Navigation
  // =========================================================

  activeTab: "new" | "requests" = "new";

  // =========================================================
  // Submission state
  // =========================================================

  submitting = false;
  submitted = false;

  formError = "";
  submittedTicketId = "";

  // =========================================================
  // Authentication
  // =========================================================

  isAuthenticated = false;
  loadingUser = true;

  user: CurrentUser | null = null;

  // =========================================================
  // Attachments
  // =========================================================

  selectedFile: File | null = null;
  attachmentError = "";

  readonly maxAttachmentSize = 5 * 1024 * 1024;

  readonly allowedAttachmentTypes = [
    "image/jpeg",
    "image/png",
    "application/pdf",
  ];

  // =========================================================
  // Categories
  // =========================================================

  readonly categories: SupportCategoryOption[] = [
    {
      value: "appointments",
      label: "Appointments & Bookings",
    },
    {
      value: "payments",
      label: "Payments & Refunds",
    },
    {
      value: "account",
      label: "Account & Login",
    },
    {
      value: "technical",
      label: "Technical Issue",
    },
    {
      value: "salon",
      label: "Salon & Services",
    },
    {
      value: "billing",
      label: "Business Billing",
    },
    {
      value: "other",
      label: "Other",
    },
  ];

  // =========================================================
  // Form
  // =========================================================

  form: SupportForm = this.createEmptyForm();

  // =========================================================
  // Tickets
  // =========================================================

  tickets: SupportTicket[] = [];

  loadingTickets = false;
  ticketsError = "";

  selectedTicket: SupportTicket | null = null;

  loadingTicket = false;
  ticketError = "";

  // =========================================================
  // Replies
  // =========================================================

  replyBody = "";
  replyAttachment: File | null = null;
  replyAttachmentError = "";

  sendingReply = false;
  replyError = "";

  // =========================================================
  // Initialization
  // =========================================================

  constructor(
    private readonly http: HttpClient,
    private readonly supportService: SupportService,
  ) {}

  ngOnInit(): void {
    this.loadUser();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // =========================================================
  // User profile
  // =========================================================

  private loadUser(): void {
    this.loadingUser = true;

    this.http
      .get<any>(`${this.apiUrl}/auth/me/`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const profile =
            response?.data?.user ??
            response?.data ??
            response?.user ??
            response;

          if (!profile?.email) {
            this.setGuestState();
            return;
          }

          this.user = {
            full_name: profile.full_name ?? profile.fullName ?? "",
            email: profile.email,
            role_name: profile.role_name,
          };

          this.isAuthenticated = true;
          this.loadingUser = false;

          this.prefillContactDetails();

          this.loadSupportTickets();
        },

        error: () => {
          this.setGuestState();
        },
      });
  }

  private setGuestState(): void {
    this.user = null;
    this.isAuthenticated = false;
    this.loadingUser = false;
    this.tickets = [];
  }

  private prefillContactDetails(): void {
    if (!this.user) {
      return;
    }

    this.form.name = this.user.full_name || "";
    this.form.email = this.user.email || "";
  }

  // =========================================================
  // Form helpers
  // =========================================================

  private createEmptyForm(): SupportForm {
    return {
      name: "",
      email: "",
      category: "",
      subject: "",
      description: "",
    };
  }

  get isFormValid(): boolean {
    return (
      this.form.name.trim().length >= 2 &&
      this.isValidEmail(this.form.email) &&
      this.form.category !== "" &&
      this.form.subject.trim().length >= 5 &&
      this.form.description.trim().length >= 10 &&
      !this.attachmentError
    );
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  // =========================================================
  // Navigation
  // =========================================================

  setActiveTab(tab: "new" | "requests"): void {
    this.activeTab = tab;
    this.formError = "";

    if (tab === "requests" && this.isAuthenticated) {
      this.loadSupportTickets();
    }
  }

  scrollToForm(): void {
    this.setActiveTab("new");

    this.scrollToWorkspace();
  }

  viewRequests(): void {
    this.setActiveTab("requests");

    this.scrollToWorkspace();
  }

  private scrollToWorkspace(): void {
    setTimeout(() => {
      document.getElementById("support-workspace")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  }

  // =========================================================
  // Attachments
  // =========================================================

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    const file = input.files?.[0];

    this.attachmentError = "";
    this.selectedFile = null;

    if (!file) {
      return;
    }

    const error = this.validateFile(file);

    if (error) {
      this.attachmentError = error;
      input.value = "";
      return;
    }

    this.selectedFile = file;
  }

  private validateFile(file: File): string {
    if (!this.allowedAttachmentTypes.includes(file.type)) {
      return "Please upload a JPG, PNG, or PDF file.";
    }

    if (file.size > this.maxAttachmentSize) {
      return "Attachments must be 5 MB or smaller.";
    }

    return "";
  }

  removeAttachment(): void {
    this.selectedFile = null;
    this.attachmentError = "";

    const input = document.getElementById(
      "support-attachment",
    ) as HTMLInputElement | null;

    if (input) {
      input.value = "";
    }
  }

  // =========================================================
  // Submit request
  // =========================================================

  submitRequest(): void {
    if (this.submitting) {
      return;
    }

    this.formError = "";
    this.submitted = false;

    if (!this.isFormValid) {
      this.formError = "Please complete all required fields before submitting.";
      return;
    }

    this.submitting = true;

    this.supportService
      .createTicket({
        name: this.form.name.trim(),
        email: this.form.email.trim(),
        category: this.form.category,
        subject: this.form.subject.trim(),
        description: this.form.description.trim(),
        attachment: this.selectedFile,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.submitting = false;

          this.submittedTicketId = response.ticket.reference;

          this.submitted = true;

          this.form = this.createEmptyForm();

          this.prefillContactDetails();

          this.removeAttachment();

          if (this.isAuthenticated) {
            this.loadSupportTickets();
          }
        },

        error: (error: HttpErrorResponse) => {
          this.submitting = false;

          this.formError = this.getApiError(
            error,
            "We couldn't submit your request. Please try again.",
          );
        },
      });
  }

  // =========================================================
  // Ticket history
  // =========================================================

  loadSupportTickets(): void {
    if (!this.isAuthenticated) {
      this.tickets = [];
      return;
    }

    this.loadingTickets = true;
    this.ticketsError = "";

    this.supportService
      .getMyTickets()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.tickets = response.results ?? [];
          this.loadingTickets = false;
        },

        error: (error: HttpErrorResponse) => {
          this.loadingTickets = false;

          this.ticketsError = this.getApiError(
            error,
            "Unable to load your support requests.",
          );
        },
      });
  }

  // =========================================================
  // Ticket details
  // =========================================================

  openTicket(ticket: SupportTicket): void {
    this.selectedTicket = null;
    this.loadingTicket = true;
    this.ticketError = "";
    this.replyError = "";
    this.replyBody = "";
    this.removeReplyAttachment();

    this.supportService
      .getTicket(ticket.ticket_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.selectedTicket = response.ticket;
          this.loadingTicket = false;
        },

        error: (error: HttpErrorResponse) => {
          this.loadingTicket = false;

          this.ticketError = this.getApiError(
            error,
            "Unable to load this support request.",
          );
        },
      });
  }

  closeTicketDetails(): void {
    this.selectedTicket = null;
    this.loadingTicket = false;
    this.ticketError = "";
    this.replyError = "";
    this.replyBody = "";
    this.removeReplyAttachment();
  }

  // =========================================================
  // Replies
  // =========================================================

  onReplyFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.replyAttachment = null;
    this.replyAttachmentError = "";

    if (!file) {
      return;
    }

    const error = this.validateFile(file);

    if (error) {
      this.replyAttachmentError = error;
      input.value = "";
      return;
    }

    this.replyAttachment = file;
  }

  removeReplyAttachment(): void {
    this.replyAttachment = null;
    this.replyAttachmentError = "";

    const input = document.getElementById(
      "support-reply-attachment",
    ) as HTMLInputElement | null;

    if (input) {
      input.value = "";
    }
  }

  get canReply(): boolean {
    return (
      !!this.selectedTicket &&
      this.selectedTicket.status !== "closed" &&
      this.replyBody.trim().length > 0 &&
      !this.replyAttachmentError &&
      !this.sendingReply
    );
  }

  submitReply(): void {
    if (!this.selectedTicket || !this.canReply) {
      return;
    }

    const ticketId = this.selectedTicket.ticket_id;

    this.sendingReply = true;
    this.replyError = "";

    this.supportService
      .replyToTicket(ticketId, this.replyBody.trim(), this.replyAttachment)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendingReply = false;
          this.replyBody = "";
          this.removeReplyAttachment();

          this.supportService
            .getTicket(ticketId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.selectedTicket = response.ticket;
                this.loadSupportTickets();
              },

              error: () => {
                this.replyError =
                  "Your reply was sent, but the conversation couldn't be refreshed.";
              },
            });
        },

        error: (error: HttpErrorResponse) => {
          this.sendingReply = false;

          this.replyError = this.getApiError(
            error,
            "Unable to send your reply.",
          );
        },
      });
  }

  // =========================================================
  // Attachment downloads
  // =========================================================

  downloadAttachment(attachmentId: string, filename: string): void {
    this.ticketError = "";

    this.supportService
      .downloadAttachment(attachmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);

          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = filename;

          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();

          URL.revokeObjectURL(url);
        },

        error: () => {
          this.ticketError = "Unable to download the attachment.";
        },
      });
  }

  // =========================================================
  // Display helpers
  // =========================================================

  getCategoryLabel(category: string): string {
    return (
      this.categories.find((item) => item.value === category)?.label ?? category
    );
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      open: "Open",
      in_progress: "In Progress",
      waiting_on_customer: "Waiting on You",
      resolved: "Resolved",
      closed: "Closed",
    };

    return labels[status] ?? status;
  }

  getPriorityLabel(priority: string): string {
    const labels: Record<string, string> = {
      low: "Low",
      normal: "Normal",
      high: "High",
      urgent: "Urgent",
    };

    return labels[priority] ?? priority;
  }

  trackByTicket(index: number, ticket: SupportTicket): string {
    return ticket.ticket_id;
  }

  trackByMessage(index: number, message: SupportMessage): string {
    return message.message_id;
  }

  // =========================================================
  // Error handling
  // =========================================================

  private getApiError(error: HttpErrorResponse, fallback: string): string {
    const data = error.error;

    if (typeof data === "string") {
      return fallback;
    }

    if (data?.detail && typeof data.detail === "string") {
      return data.detail;
    }

    if (data?.message && typeof data.message === "string") {
      return data.message;
    }

    if (data && typeof data === "object") {
      for (const value of Object.values(data)) {
        if (Array.isArray(value) && value.length) {
          return String(value[0]);
        }

        if (typeof value === "string") {
          return value;
        }
      }
    }

    return fallback;
  }

  // =========================================================
  // Reset
  // =========================================================

  createAnotherRequest(): void {
    this.submitted = false;
    this.submittedTicketId = "";
    this.formError = "";

    this.form = this.createEmptyForm();

    this.prefillContactDetails();

    this.removeAttachment();

    this.scrollToForm();
  }
}
